import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Chip, LoadingScreen } from '@cowoo/shared/ui-web';
import { formatDate, formatTime, qk } from '@cowoo/shared';
import { api } from '../../lib/api';
import { EmptyState, ListCard, Page, Table, Td, Th, Icon } from '../../ui';

/**
 * `/audit` (10-web-admin § 19).
 *
 * Read-only, and no export in v1 — the log is evidence, and a screen that can
 * write to it or take copies of it away is a worse one.
 *
 * A row expands to the before/after the server recorded. The values are shown
 * as they are delivered, including personal data: an audit entry that hid what
 * actually changed would not be an audit entry (D-034, D-077).
 */
export function Audit() {
  const { t, i18n } = useTranslation();

  /**
   * The German label for an action, or the raw key.
   *
   * `defaultValue` is not enough: the i18n setup throws on a missing key so
   * that a typo is caught in development, which means an action added on the
   * server and not yet translated here took the whole screen down with it. The
   * audit log renders whatever actions exist — it cannot be the screen that
   * breaks when a new one appears.
   */
  const actionLabel = (action: string): string => {
    const key = `admin.audit.actionLabel.${action}`;
    return i18n.exists(key) ? t(key) : action;
  };
  const [action, setAction] = useState('');
  const [actor, setActor] = useState('');
  const [q, setQ] = useState('');
  /**
   * The newest entry starts expanded.
   *
   * The whole question this screen answers is "what changed", and a log that
   * hides that behind a click answers it only for somebody who already knows
   * to click. The artboard draws it open for the same reason.
   */
  const [open, setOpen] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  const log = useQuery({
    queryKey: qk.admin.area('audit', { action, actor, q }),
    queryFn: async () => {
      const { data, error } = await api.GET('/admin/audit-log', {
        params: {
          query: {
            ...(action ? { action } : {}),
            ...(actor ? { actorUserId: actor } : {}),
            ...(q ? { q } : {}),
          },
        },
      });
      if (error) throw error;
      return data;
    },
  });

  if (log.isLoading) return <LoadingScreen label={t('common.loading')} />;

  const items = log.data?.items ?? [];
  const expanded = touched ? open : (open ?? items[0]?.id ?? null);

  return (
    <Page>
      <ListCard
        title={t('admin.audit.title')}
        actions={
          <>
            <select
              value={actor}
              onChange={(event) => setActor(event.target.value)}
              aria-label={t('admin.audit.actor')}
              className="border-line rounded-input text-adminBody h-9 border bg-white px-3 font-semibold"
            >
              <option value="">{t('admin.audit.allActors')}</option>
              {(log.data?.actors ?? []).map((entry) => (
                <option key={entry.userId} value={entry.userId}>
                  {entry.name ?? entry.userId}
                </option>
              ))}
            </select>
            <select
              value={action}
              onChange={(event) => setAction(event.target.value)}
              aria-label={t('admin.audit.action')}
              className="border-line rounded-input text-adminBody h-9 border bg-white px-3 font-semibold"
            >
              <option value="">{t('admin.audit.allActions')}</option>
              {(log.data?.actions ?? []).map((value) => (
                <option key={value} value={value}>
                  {actionLabel(value)}
                </option>
              ))}
            </select>
            <label className="border-line rounded-input text-placeholder flex h-9 w-[220px] items-center gap-2 border bg-white px-3">
              <Icon name="search" size={14} />
              <input
                value={q}
                onChange={(event) => setQ(event.target.value)}
                placeholder={t('admin.audit.search')}
                aria-label={t('admin.audit.search')}
                className="text-adminBody text-ink placeholder:text-placeholder min-w-0 flex-1 bg-transparent outline-none"
              />
            </label>
          </>
        }
      >
        {items.length === 0 ? (
          <EmptyState>{t('admin.audit.empty')}</EmptyState>
        ) : (
          <Table
            head={
              <>
                <Th>{t('admin.audit.column.when')}</Th>
                <Th>{t('admin.audit.column.who')}</Th>
                <Th>{t('admin.audit.column.action')}</Th>
                <Th>{t('admin.audit.column.target')}</Th>
                <Th>{t('admin.audit.column.reason')}</Th>
                <Th>{t('admin.audit.column.ip')}</Th>
              </>
            }
          >
            {items.map((entry) => (
              <>
                <tr
                  key={entry.id}
                  onClick={() => {
                    setTouched(true);
                    setOpen(expanded === entry.id ? null : entry.id);
                  }}
                  className="hover:bg-fog cursor-pointer"
                >
                  <Td>
                    <span className="tabular-nums">
                      {formatDate(entry.ts)} {formatTime(entry.ts)}
                    </span>
                  </Td>
                  <Td>
                    {entry.actorName ?? '—'}
                    {entry.actorRole ? (
                      <span className="ml-2">
                        <Chip tone="teal" label={entry.actorRole} />
                      </span>
                    ) : null}
                  </Td>
                  {/* The raw key is the tooltip, as § 19 asks: a label can be
                      wrong, the key is what was written. */}
                  <Td bold>
                    <span title={entry.action}>{actionLabel(entry.action)}</span>
                  </Td>
                  <Td>{entry.targetType}</Td>
                  <Td>{entry.reason ?? '—'}</Td>
                  <Td>
                    <span className="flex items-center justify-between gap-2">
                      <span className="tabular-nums">{entry.ip ?? '—'}</span>
                      {/* The board ends every row with a chevron that turns
                          when the row is open — the row is a disclosure. */}
                      <Icon
                        name="chevronRight"
                        size={16}
                        className={`text-muted transition-transform ${expanded === entry.id ? 'rotate-90' : ''}`}
                      />
                    </span>
                  </Td>
                </tr>
                {expanded === entry.id ? (
                  <tr key={`${entry.id}-diff`}>
                    <td colSpan={6} className="pb-3">
                      <div className="border-line rounded-input grid grid-cols-2 gap-3 border bg-white p-3.5">
                        <Side label={t('admin.audit.before')} tone="danger" value={entry.before} />
                        <Side label={t('admin.audit.after')} tone="success" value={entry.after} />
                      </div>
                    </td>
                  </tr>
                ) : null}
              </>
            ))}
          </Table>
        )}
        <p className="text-adminSmall text-muted pt-1">{t('admin.audit.readOnly')}</p>
      </ListCard>
    </Page>
  );
}

function Side({ label, tone, value }: { label: string; tone: 'danger' | 'success'; value: unknown }) {
  const { t } = useTranslation();
  return (
    <div>
      <p className="text-label text-muted pb-1.5 uppercase">{label}</p>
      <pre
        className={`rounded-segmentInner overflow-x-auto px-2.5 py-2 font-mono text-xs leading-relaxed ${
          tone === 'danger' ? 'bg-danger-bg text-danger-text' : 'bg-success-bg text-success-text'
        }`}
      >
        {value === null || value === undefined ? t('admin.audit.nothing') : JSON.stringify(value, null, 2)}
      </pre>
    </div>
  );
}
